import { Link } from "react-router-dom";
import AuthImage from "@/components/AuthImage";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";

/**
 * Shared boat card used by Explore grid, Landing marquee AND recommendations.
 * variant = "grid" (default, aspect 4/5) | "marquee" (fixed width for horizontal scroll)
 * reason   = optional badge text shown over the image (recommendation motive)
 * tone     = "gold" (default recommendation) | "muted" (out of budget)
 */
export default function BoatCard({ boat, variant = "grid", testIdPrefix = "boat-card", reason = "", tone = "gold" }) {
  const { t } = useLang();
  const primary = boat.images?.find((i) => i.is_primary) || boat.images?.[0];
  const widthCls = variant === "marquee" ? "w-[280px] sm:w-[320px] md:w-[360px] shrink-0" : "";
  const mode = boat.ownership_mode || boat.modality;
  const modality = mode === "full" ? t.explore.modalityFull : t.explore.modalityCoownership;
  const badgeCls = tone === "muted"
    ? "bg-white/90 text-slate-500 border border-slate-200"
    : "bg-[#0B192C] text-white";

  return (
    <Link
      to={`/explorar/${boat.id}`}
      className={`group block ${widthCls}`}
      data-testid={`${testIdPrefix}-${boat.id}`}
    >
      <div className="aspect-[4/5] rounded-2xl overflow-hidden bg-slate-100 relative">
        {reason && (
          <span
            className={`absolute top-3 left-3 z-10 px-3 py-1 rounded-full text-[10px] font-display uppercase tracking-widest ${badgeCls}`}
            data-testid={`${testIdPrefix}-reason-${boat.id}`}
          >
            {reason}
          </span>
        )}
        {primary ? (
          <AuthImage
            path={primary.storage_path}
            className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-[1.04]"
          />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-400 text-xs font-display">
            — no photo —
          </div>
        )}
      </div>
      <div className="mt-5">
        <p className="text-[10px] font-display uppercase tracking-widest text-[#C2A878]">
          {t.boats.types[boat.type]}
          {boat.length ? ` · ${boat.length} m` : ""}
          {` · ${modality}`}
        </p>
        <h3 className="font-serif text-xl md:text-2xl mt-2 text-[#0B192C] leading-tight">
          {boat.brand} {boat.model}
        </h3>
        <p className="mt-1 text-sm text-slate-500">
          {boat.location}
          {boat.port ? ` · ${boat.port}` : ""}
          {boat.country ? `, ${boat.country}` : ""}
        </p>
        <div className="mt-4 flex items-baseline justify-between border-t border-slate-100 pt-4">
          <span className="text-[10px] font-display uppercase tracking-widest text-slate-400">
            {t.explore.sharePrice}
          </span>
          <span className="font-serif text-lg text-[#0B192C]">
            €{Number(boat.share_price || 0).toLocaleString()}
          </span>
        </div>
        <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#0B192C] group-hover:gap-2 transition-all">
          {t.explore.viewBoat} <ArrowRight className="h-3.5 w-3.5" />
        </span>
      </div>
    </Link>
  );
}
